import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Select,
  FundraisingMetricsGrid,
  FundraisingProgressTracker,
  BarList,
  FundraisingLeaderboard,
  FundraisingRaiseComparison,
  FunnelChart,
  type LeaderboardItem,
  type RaiseComparisonItem,
} from "@repo/o37-ui";
import { TrendingUp, Target, Calendar, DollarSign, Users, FileText, BarChart3 } from "lucide-react";
import { Fundraise, FundraisingFund, FundraisingAnalytics, FUND_STAGES, FUND_PRIORITIES } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface RaiseWithAnalytics extends Fundraise {
  analytics?: FundraisingAnalytics;
  funds?: FundraisingFund[];
}

export default function AllRaisesAnalytics() {
  const navigate = useNavigate();
  const [raises, setRaises] = useState<RaiseWithAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState<'all' | 'active' | 'closed'>('all');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      // Load all raises
      const raisesResponse = await fetch(`${API_URL}/fundraising/raises`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      
      if (!raisesResponse.ok) {
        throw new Error('Failed to load raises');
      }

      const raisesData = await raisesResponse.json();
      
      // Load analytics and funds for each raise
      const raisesWithData: RaiseWithAnalytics[] = await Promise.all(
        raisesData.map(async (r: any) => {
          const [analyticsRes, fundsRes] = await Promise.all([
            fetch(`${API_URL}/fundraising/raises/${r.id}/analytics`, {
              headers: { 'x-user-id': userId, 'x-user-role': userRole }
            }),
            fetch(`${API_URL}/fundraising/raises/${r.id}/funds`, {
              headers: { 'x-user-id': userId, 'x-user-role': userRole }
            })
          ]);

          let analytics: FundraisingAnalytics | undefined;
          let funds: FundraisingFund[] = [];

          if (analyticsRes.ok) {
            analytics = await analyticsRes.json();
          }

          if (fundsRes.ok) {
            const fundsData = await fundsRes.json();
            funds = fundsData.map((f: any) => ({
              id: f.id,
              fundraiseId: f.fundraise_id,
              name: f.name,
              stage: f.stage,
              priority: f.priority,
              typicalTicketMin: f.typical_ticket_min,
              typicalTicketMax: f.typical_ticket_max,
              createdAt: f.created_at,
              updatedAt: f.updated_at,
              termSheets: f.fundraising_term_sheets?.map((ts: any) => ({
                id: ts.id,
                investmentAmount: ts.investment_amount,
                preMoneyValuation: ts.pre_money_valuation,
                status: ts.status,
                receivedAt: ts.received_at
              }))
            }));
          }

          return {
            id: r.id,
            name: r.name,
            description: r.description,
            status: r.status,
            raiseGoal: r.raise_goal,
            linkedVdrId: r.linked_vdr_id,
            createdAt: r.created_at,
            updatedAt: r.updated_at,
            analytics,
            funds
          };
        })
      );

      setRaises(raisesWithData);
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter raises by status
  const filteredRaises = raises.filter(r => {
    if (timeFilter === 'all') return true;
    if (timeFilter === 'active') return r.status === 'active';
    if (timeFilter === 'closed') return r.status === 'closed';
    return true;
  });

  // Calculate aggregated metrics
  const aggregatedMetrics = filteredRaises.reduce((acc, raise) => {
    if (raise.analytics) {
      acc.totalFunds += raise.analytics.totalFunds;
      acc.activeFunds += raise.analytics.activeFunds;
      acc.totalTermSheets += raise.analytics.totalTermSheets;
      acc.committedCapital += raise.analytics.committedCapital;
      acc.pendingCapital += raise.analytics.pendingCapital;
      acc.raiseGoal += raise.analytics.raiseGoal;
      
      // Aggregate stage counts
      Object.entries(raise.analytics.stageCounts).forEach(([stage, count]) => {
        acc.stageCounts[stage as keyof typeof acc.stageCounts] = 
          (acc.stageCounts[stage as keyof typeof acc.stageCounts] || 0) + count;
      });

      // Aggregate priority counts
      Object.entries(raise.analytics.priorityCounts).forEach(([priority, count]) => {
        acc.priorityCounts[priority as keyof typeof acc.priorityCounts] = 
          (acc.priorityCounts[priority as keyof typeof acc.priorityCounts] || 0) + count;
      });
    }
    return acc;
  }, {
    totalFunds: 0,
    activeFunds: 0,
    totalTermSheets: 0,
    committedCapital: 0,
    pendingCapital: 0,
    raiseGoal: 0,
    stageCounts: {} as Record<string, number>,
    priorityCounts: {} as Record<string, number>
  });

  // Get all term sheets across all raises
  const allTermSheets = filteredRaises.flatMap(raise => 
    (raise.funds || []).flatMap(fund => 
      (fund.termSheets || []).map(ts => ({
        ...ts,
        fundName: fund.name,
        raiseName: raise.name,
        raiseId: raise.id
      }))
    )
  ).sort((a, b) => (b.investmentAmount || 0) - (a.investmentAmount || 0));

  const overallProgress = aggregatedMetrics.raiseGoal > 0 
    ? (aggregatedMetrics.committedCapital / aggregatedMetrics.raiseGoal * 100)
    : 0;

  // Transform data for components
  const summaryMetrics = [
    {
      label: "Total Raises",
      value: filteredRaises.length.toString(),
      hint: `${filteredRaises.filter(r => r.status === 'active').length} active`,
      icon: <Calendar className="w-3.5 h-3.5" />,
    },
    {
      label: "Total Funds",
      value: aggregatedMetrics.totalFunds.toString(),
      hint: `${aggregatedMetrics.activeFunds} in pipeline`,
      icon: <Users className="w-3.5 h-3.5" />,
    },
    {
      label: "Term Sheets",
      value: aggregatedMetrics.totalTermSheets.toString(),
      hint: "Total received",
      icon: <FileText className="w-3.5 h-3.5" />,
    },
    {
      label: "Total Committed",
      value: formatCurrency(aggregatedMetrics.committedCapital),
      hint: "Accepted terms",
      icon: <DollarSign className="w-3.5 h-3.5" />,
    },
    {
      label: "Pipeline Value",
      value: formatCurrency(aggregatedMetrics.pendingCapital),
      hint: "Pending review",
      icon: <Target className="w-3.5 h-3.5" />,
    },
    {
      label: "Combined Goal",
      value: formatCurrency(aggregatedMetrics.raiseGoal),
      hint: `${overallProgress.toFixed(0)}% achieved`,
      icon: <TrendingUp className="w-3.5 h-3.5" />,
    },
  ];

  const stageBarData = FUND_STAGES.map(({ stage, label, bgColor }) => ({
    icon: null,
    title: label,
    value: aggregatedMetrics.stageCounts[stage] || 0,
    itemBarBackground: bgColor.replace('-50', '-400'),
  }));

  const priorityBarData = FUND_PRIORITIES.map(({ priority, label, color }) => ({
    icon: null,
    title: label,
    value: aggregatedMetrics.priorityCounts[priority] || 0,
    itemBarBackground: color,
  }));

  const raiseComparisonItems: RaiseComparisonItem[] = filteredRaises.map(raise => ({
    id: raise.id,
    name: raise.name,
    status: raise.status as RaiseComparisonItem['status'],
    current: raise.analytics?.committedCapital || 0,
    goal: raise.raiseGoal || 0,
    fundsCount: raise.analytics?.totalFunds || 0,
    onClick: () => navigate(`/fundraising/raises/${raise.id}`),
  }));

  const leaderboardItems: LeaderboardItem[] = allTermSheets.slice(0, 10).map((ts, index) => ({
    id: ts.id,
    rank: index + 1,
    name: ts.fundName,
    subtitle: ts.raiseName,
    value: ts.investmentAmount,
    secondaryValue: ts.receivedAt ? formatDate(ts.receivedAt) : undefined,
    status: ts.status as LeaderboardItem['status'],
    onClick: () => navigate(`/fundraising/raises/${ts.raiseId}`),
  }));

  const funnelSteps = FUND_STAGES.filter(s => s.stage !== 'uninterested').map(({ stage, label, color, description }) => ({
    id: stage,
    label,
    value: aggregatedMetrics.stageCounts[stage] || 0,
    colorClassName: color,
    description,
  }));

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Analytics</h1>
        <p className="text-neutral-500">Loading comprehensive analytics...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Fundraising Analytics</h1>
          <p className="text-neutral-500">Comprehensive view across all fundraising campaigns</p>
        </div>
        <Select
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value as any)}
          className="w-40"
        >
          <option value="all">All Raises</option>
          <option value="active">Active Only</option>
          <option value="closed">Closed Only</option>
        </Select>
      </div>

      {/* Summary Stats */}
      <FundraisingMetricsGrid metrics={summaryMetrics} columns={6} className="mb-8" />

      {/* Progress Overview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Overall Capital Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <FundraisingProgressTracker
            current={aggregatedMetrics.committedCapital}
            goal={aggregatedMetrics.raiseGoal}
            pending={aggregatedMetrics.pendingCapital}
            formatCurrency={formatCurrency}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Raise-by-Raise Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Raise Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <FundraisingRaiseComparison
              raises={raiseComparisonItems}
              formatCurrency={formatCurrency}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Pipeline by Stage */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Overall Pipeline Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <BarList
              tab="pipeline"
              unit="funds"
              data={stageBarData}
              maxValue={Math.max(...stageBarData.map(d => d.value), 1)}
              barBackground="bg-neutral-100"
              hoverBackground="hover:bg-neutral-50"
              setShowModal={() => {}}
            />
          </CardContent>
        </Card>

        {/* Priority Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Fund Tier Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <BarList
              tab="priority"
              unit="funds"
              data={priorityBarData}
              maxValue={Math.max(...priorityBarData.map(d => d.value), 1)}
              barBackground="bg-neutral-100"
              hoverBackground="hover:bg-neutral-50"
              setShowModal={() => {}}
            />
          </CardContent>
        </Card>
      </div>

      {/* Conversion Funnel */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Overall Conversion Funnel
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <FunnelChart steps={funnelSteps} height={300} showLegend />
        </CardContent>
      </Card>

      {/* Top Term Sheets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Top Term Sheets (All Raises)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <FundraisingLeaderboard
            items={leaderboardItems}
            formatValue={formatCurrency}
            emptyMessage="No term sheets received yet across any raises"
            maxItems={10}
          />
        </CardContent>
      </Card>
    </div>
  );
}
